import './chat.css';

import {
  CHAT_MAX_LENGTH,
  type ChatBroadcast,
  type ChatChannel,
  type ChatRejected,
  type ItemInstance,
  MAX_UPGRADE_LEVEL,
  upgradeColor,
} from '@shefira/shared';

import { GM_NAME_COLOR, gmName } from '../game/gm.js';
import { followPreference } from '../settings/preferences.js';
import { channelTargets, rememberCorrespondent, sameTarget, stepTarget } from './chat-channels.js';
import type { ChatTarget } from './chat-channels.js';
import { ChatDraft } from './chat-draft.js';
import { appendChatText, itemLinkLabel } from './chat-links.js';
import { iconButton } from './hud.js';
import { uiFormat, uiHtml, uiText } from './text.js';
import { isTouch } from './touch.js';

/**
 * The chat panel: tabs, a short log and an input line (ADR 0033).
 *
 * DOM for the same reason as the login screen: a text field needs focus, IME,
 * selection and a mobile keyboard, none of which a canvas gives for free. The
 * panel sits over the canvas; the world behind it keeps taking clicks.
 *
 * Each line belongs to a channel: everyone (`general`), the zone, the party,
 * the guild or a private exchange. A tab shows one channel, « Tout » all of them;
 * tabs only filter what is read, the button before the input, Tab, or a click on
 * a name chooses where one speaks. The server decides who hears a line; the
 * panel only remembers where the player last chose to speak.
 */

/** Lines kept in the log; older ones scroll away. */
const LOG_CAPACITY = 60;
/** A line stops drawing attention this long after it arrived. */
const FADE_AFTER_MS = 12_000;

type Tab = 'all' | ChatChannel;

const TABS: readonly { tab: Tab; label: string }[] = [
  { tab: 'all', label: 'Tout' },
  { tab: 'general', label: 'Général' },
  { tab: 'zone', label: 'Zone' },
  { tab: 'party', label: 'Groupe' },
  { tab: 'guild', label: 'Guilde' },
  { tab: 'private', label: 'Privé' },
];

const CHANNEL_NAMES: Record<ChatChannel, string> = {
  general: 'Général',
  zone: 'Zone',
  party: 'Groupe',
  guild: 'Guilde',
  private: 'Privé',
};

export type { ChatTarget } from './chat-channels.js';

export interface ChatPanelOptions {
  /** The local player's session id, so their own lines can be styled apart. */
  selfSessionId: string;
  /**
   * The line as typed and where the panel would send it; the caller reads
   * commands in it. `links`: the ids of the items its `[#n]` tokens stand for.
   */
  onSend: (text: string, target: ChatTarget, links: readonly string[]) => void;
}

/**
 * What a chat hands to the next one on a zone change: entering a cave or a
 * dungeon stays in the same world, so the conversation goes on.
 */
interface CarriedChat {
  lines: HTMLElement[];
  shown: Tab;
  target: ChatTarget;
  lastWhisperer?: string;
  recent: string[];
}

let carried: CarriedChat | undefined;

export class ChatPanel {
  private readonly root: HTMLElement;
  private readonly log: HTMLElement;
  private readonly input: HTMLInputElement;
  private readonly chip: HTMLButtonElement;
  private readonly channels: HTMLElement;
  private readonly tabs = new Map<Tab, HTMLButtonElement>();
  private readonly fades = new WeakMap<HTMLElement, number>();
  private readonly onSend: ChatPanelOptions['onSend'];
  private readonly selfSessionId: string;
  private readonly unfollow: () => void;
  /** The items linked in the line being typed (ADR 0085). */
  private readonly draft = new ChatDraft();
  private shown: Tab = 'all';
  private target: ChatTarget = { channel: 'zone' };
  /** Who last wrote to the player in private, for « /r ». */
  private lastWhisperer?: string;
  /** The last private correspondents, newest first, for the channel list. */
  private recent: string[] = [];
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    this.handleGlobalKey(event);
  };
  /**
   * A click anywhere outside the chat hands the keys back to the game. Phaser
   * cancels the canvas's pointerdown, so the browser alone never blurs the input
   * and the spell keys would keep landing in it.
   */
  private readonly onPointerDown = (event: PointerEvent): void => {
    const inside = event.target instanceof Node && this.root.contains(event.target);
    if (
      !this.channels.hidden &&
      !(
        event.target instanceof Node &&
        (this.channels.contains(event.target) || this.chip.contains(event.target))
      )
    ) {
      this.closeChannels();
    }
    if (document.activeElement !== this.input || inside) return;
    this.input.blur();
  };

  constructor(options: ChatPanelOptions) {
    this.onSend = options.onSend;
    this.selfSessionId = options.selfSessionId;

    this.root = document.createElement('section');
    this.root.className = 'chat';
    this.root.setAttribute('aria-label', uiText('Chat'));
    this.root.innerHTML = uiHtml(`
      <div class="chat__tabs" role="tablist"></div>
      <ol class="chat__log" aria-live="polite"></ol>
      <form class="chat__form" novalidate>
        <button type="button" class="chat__chip" aria-haspopup="menu"></button>
        <input class="chat__input" name="text" autocomplete="off" autocapitalize="sentences"
               spellcheck="true" maxlength="${String(CHAT_MAX_LENGTH)}"
               placeholder="${isTouch() ? 'Écris un message' : 'Entrée pour parler'}" aria-label="Message" />
      </form>
    `);
    document.body.append(this.root);

    const tabs = this.root.querySelector<HTMLElement>('.chat__tabs');
    const log = this.root.querySelector<HTMLElement>('.chat__log');
    const input = this.root.querySelector<HTMLInputElement>('.chat__input');
    const form = this.root.querySelector<HTMLFormElement>('.chat__form');
    const chip = this.root.querySelector<HTMLButtonElement>('.chat__chip');
    if (tabs === null || log === null || input === null || form === null || chip === null) {
      throw new Error('Chat markup is missing a node');
    }
    this.log = log;
    this.input = input;
    this.chip = chip;
    this.channels = document.createElement('div');
    this.channels.className = 'chat__channels';
    this.channels.setAttribute('role', 'menu');
    this.channels.setAttribute('aria-label', uiText('Canaux'));
    this.channels.hidden = true;
    form.prepend(this.channels);
    chip.addEventListener('click', () => {
      this.toggleChannels();
    });

    // On a touch screen the chat folds to a button and its last lines
    // (ADR 0043): no Enter key opens it, and an open chat would cover half a
    // phone. The keyboard's own Enter sends; the arrow button does too, for
    // keyboards that have none. Both are hidden on a desktop (touch.css).
    const toggle = iconButton('chat', 'Chat');
    toggle.classList.add('chat__toggle');
    toggle.addEventListener('click', () => {
      this.setOpen(!this.root.classList.contains('chat--open'));
    });
    const send = document.createElement('button');
    send.type = 'submit';
    send.className = 'hud-button chat__send';
    send.textContent = uiText('Envoyer');
    form.prepend(toggle);
    form.append(send);

    for (const { tab, label } of TABS) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'chat__tab';
      button.dataset.channel = tab;
      button.setAttribute('role', 'tab');
      button.textContent = uiText(label);
      button.addEventListener('click', () => {
        this.showTab(tab);
      });
      tabs.append(button);
      this.tabs.set(tab, button);
    }

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submit();
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.channels.hidden) {
        this.closeChannels();
        event.stopPropagation();
        return;
      }
      if (event.key === 'Escape') {
        input.value = '';
        this.forgetLinks();
        this.setOpen(false);
      }
      if (event.key === 'Tab') {
        event.preventDefault();
        this.setTarget(stepTarget(this.target, this.recent, event.shiftKey ? -1 : 1));
      }
      // Keep typing from reaching the game: a space or an arrow in a sentence
      // is not a command.
      event.stopPropagation();
    });
    input.addEventListener('focus', () => {
      this.root.classList.add('chat--typing');
    });
    input.addEventListener('blur', () => {
      this.root.classList.remove('chat--typing');
    });
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('pointerdown', this.onPointerDown, true);
    const previous = carried;
    carried = undefined;
    if (previous !== undefined) {
      this.log.append(...previous.lines);
      this.lastWhisperer = previous.lastWhisperer;
      this.recent = previous.recent;
      this.target = previous.target;
    }
    this.showTab(previous?.shown ?? 'all');
    this.renderTarget();
    // Hidden by the display preference (ADR 0029): nothing shows until the
    // player types; Enter and the touch button still open the input.
    this.unfollow = followPreference('chat', (enabled) => {
      this.root.classList.toggle('chat--hidden', !enabled);
    });
  }

  /** Where Enter sends the next line. */
  get current(): ChatTarget {
    return this.target;
  }

  /** The last player who wrote in private, for « /r ». */
  get replyTo(): string | undefined {
    return this.lastWhisperer;
  }

  /** Speak on `target` from now on; the chip before the input says where. */
  setTarget(target: ChatTarget): void {
    this.target = target;
    this.renderTarget();
  }

  /** Open the input, ready to type (a « Message » button in the friends' list). */
  focus(): void {
    this.setOpen(true);
  }

  /**
   * Shift+click on an item, or « Lier dans le chat » on its card (ADR 0085):
   * its name in brackets where the cursor is, linked when the line is sent.
   */
  linkItem(item: ItemInstance): void {
    const label = itemLinkLabel(item);
    if (!this.draft.add(item.id, label)) {
      this.notice('Trois objets au plus par message.');
      return;
    }
    this.input.maxLength = CHAT_MAX_LENGTH + this.draft.extraLength;
    const start = this.input.selectionStart ?? this.input.value.length;
    const end = this.input.selectionEnd ?? start;
    const before = this.input.value.slice(0, start);
    const spaced = before.length > 0 && !before.endsWith(' ') ? ` ${label}` : label;
    this.input.value = before + spaced + this.input.value.slice(end);
    this.setOpen(true);
    const cursor = before.length + spaced.length;
    this.input.setSelectionRange(cursor, cursor);
  }

  private forgetLinks(): void {
    this.draft.clear();
    this.input.maxLength = CHAT_MAX_LENGTH;
  }

  /** Unfolds the chat (a touch screen folds it) and puts the cursor in it, or folds it back. */
  private setOpen(open: boolean): void {
    this.root.classList.toggle('chat--open', open);
    if (open) {
      this.input.focus();
      this.log.scrollTop = this.log.scrollHeight;
    } else {
      this.input.blur();
    }
  }

  private showTab(tab: Tab): void {
    this.shown = tab;
    this.root.dataset.shown = tab;
    for (const [each, button] of this.tabs) {
      button.classList.toggle('chat__tab--active', each === tab);
      button.setAttribute('aria-selected', String(each === tab));
      if (each === tab) button.classList.remove('chat__tab--unread');
    }
    this.log.scrollTop = this.log.scrollHeight;
  }

  private renderTarget(): void {
    const { channel, to } = this.target;
    this.chip.dataset.channel = channel;
    this.chip.title = uiText(isTouch() ? 'Changer de canal' : 'Changer de canal (Tab)');
    this.chip.textContent =
      channel === 'private' && to !== undefined
        ? uiFormat('À {name}', { name: to })
        : uiText(CHANNEL_NAMES[channel]);
  }

  private toggleChannels(): void {
    if (!this.channels.hidden) {
      this.closeChannels();
      return;
    }
    this.channels.replaceChildren(
      ...channelTargets(this.recent).map((target) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'chat__channel';
        item.setAttribute('role', 'menuitem');
        item.dataset.channel = target.channel;
        item.textContent =
          target.channel === 'private' && target.to !== undefined
            ? uiFormat('À {name}', { name: target.to })
            : uiText(CHANNEL_NAMES[target.channel]);
        if (sameTarget(target, this.target)) item.classList.add('chat__channel--current');
        item.addEventListener('click', () => {
          this.setTarget(target);
          this.closeChannels();
          this.input.focus();
        });
        return item;
      }),
    );
    this.channels.hidden = false;
    this.chip.setAttribute('aria-expanded', 'true');
  }

  private closeChannels(): void {
    this.channels.hidden = true;
    this.chip.setAttribute('aria-expanded', 'false');
  }

  /** Enter anywhere on the page opens the input; nothing else is captured. */
  private handleGlobalKey(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || document.activeElement === this.input) return;
    if (document.activeElement instanceof HTMLInputElement) return;
    event.preventDefault();
    this.setOpen(true);
  }

  private submit(): void {
    const sealed = this.draft.seal(this.input.value.trim());
    this.input.value = '';
    this.forgetLinks();
    if (sealed.text.length === 0) {
      // Enter on an empty line closes the chat, like Enter opened it.
      this.setOpen(false);
      return;
    }
    this.onSend(sealed.text, this.target, sealed.links);
    // Stay focused: people usually send more than one line in a row.
  }

  append(message: ChatBroadcast): void {
    const line = this.line(message.channel);
    const self = message.from === this.selfSessionId;
    if (self) line.classList.add('chat__line--self');
    if (message.channel === 'private') {
      if (!self) this.lastWhisperer = message.name;
      const other = self ? (message.to ?? '') : message.name;
      if (other.length > 0) this.recent = rememberCorrespondent(this.recent, other);
    }

    // Where it was said, unless it is the zone's own talk.
    const tag = this.tagOf(message, self);
    if (tag !== undefined) {
      const span = document.createElement('span');
      span.className = 'chat__tag';
      span.textContent = `[${tag}] `;
      line.append(span);
    }
    // One's own private line reads « [À Bob] texte », without one's own name.
    if (!(self && message.channel === 'private')) {
      const name = document.createElement('span');
      name.className = 'chat__name';
      name.textContent = uiText(gmName(message.name, message.gm));
      if (!self) {
        name.classList.add('chat__name--link');
        name.title = uiText('Message privé');
        name.addEventListener('click', () => {
          this.setTarget({ channel: 'private', to: message.name });
          this.setOpen(true);
        });
      }
      // From an aura of +3 the name takes its colour (ADR 0013); a game master's is violet.
      const color = message.gm ? GM_NAME_COLOR : upgradeColor(message.aura);
      if (color !== undefined) name.style.color = `#${color.toString(16).padStart(6, '0')}`;
      line.append(name);
      // The speaker's guild tag (ADR 0036), dimmer than the name so it reads as a badge.
      if (message.guild !== undefined && message.guild.length > 0) {
        const guild = document.createElement('span');
        guild.className = 'chat__guild';
        guild.textContent = uiText(`[${message.guild}]`);
        line.append(guild);
      }
    }
    const text = document.createElement('span');
    text.className = 'chat__text';
    appendChatText(text, message.text, message.links);
    line.append(text);
    this.push(line);
  }

  /** The line's tag, already in the player's language. */
  private tagOf(message: ChatBroadcast, self: boolean): string | undefined {
    switch (message.channel) {
      case 'general':
        return uiText('Général');
      case 'party':
        return uiText('Groupe');
      case 'guild':
        return uiText('Guilde');
      case 'private':
        return self ? uiFormat('À {name}', { name: message.to ?? '?' }) : uiText('De');
      case 'zone':
        return undefined;
    }
  }

  /** A line from the game itself (a rejection, a player arriving), not from a player. */
  notice(text: string): void {
    const said = uiText(text);
    // The same notice twice in a row counts up on its line instead of filling the log.
    const last = this.log.lastElementChild;
    if (last instanceof HTMLElement && last.dataset.notice === said) {
      const count = Number(last.dataset.count ?? '1') + 1;
      last.dataset.count = String(count);
      last.textContent = `${said} ×${String(count)}`;
      this.fadeLater(last);
      return;
    }
    const line = this.line(undefined);
    line.classList.add('chat__line--notice');
    line.dataset.notice = said;
    line.textContent = said;
    this.push(line);
  }

  /**
   * A server announcement to everyone (a +8 at the smith, ADR 0013): a notice
   * in the gold of a +10, so the feat stands out of the log.
   */
  announce(text: string): void {
    const line = this.line(undefined);
    line.classList.add('chat__line--notice');
    const color = upgradeColor(MAX_UPGRADE_LEVEL) ?? 0xffffff;
    line.style.color = `#${color.toString(16).padStart(6, '0')}`;
    line.textContent = uiText(text);
    this.push(line);
  }

  rejected(rejection: ChatRejected): void {
    if (rejection.reason === 'no-party') {
      this.notice("Tu n'es pas dans un groupe.");
    } else if (rejection.reason === 'no-guild') {
      this.notice("Tu n'es pas dans une guilde.");
    } else if (rejection.reason === 'muted') {
      const minutes = Math.max(1, Math.ceil((rejection.retryInMs ?? 0) / 60_000));
      this.notice(uiFormat('Tu es réduit au silence encore {minutes} min.', { minutes }));
    } else if (rejection.reason === 'no-recipient') {
      this.notice("Personne de ce nom n'est en jeu.");
    } else if (rejection.reason === 'no-item') {
      this.notice("Cet objet n'est plus à toi.");
    } else if (rejection.reason === 'rate-limited') {
      const seconds = Math.max(1, Math.ceil((rejection.retryInMs ?? 0) / 1000));
      this.notice(uiFormat('Tu parles trop vite. Réessaie dans {seconds} s.', { seconds }));
    } else {
      this.notice('Message refusé.');
    }
  }

  /** A log line of `channel`; a notice (no channel) shows in every tab. */
  private line(channel: ChatChannel | undefined): HTMLLIElement {
    const line = document.createElement('li');
    line.className = 'chat__line';
    if (channel !== undefined) {
      line.dataset.channel = channel;
      const tab = this.tabs.get(channel);
      if (this.shown !== 'all' && this.shown !== channel) tab?.classList.add('chat__tab--unread');
    }
    return line;
  }

  private push(line: HTMLElement): void {
    // Someone reading back up the log stays where they are.
    const atBottom = this.log.scrollHeight - this.log.scrollTop - this.log.clientHeight < 24;
    this.log.append(line);
    while (this.log.childElementCount > LOG_CAPACITY) this.log.firstElementChild?.remove();
    if (atBottom) this.log.scrollTop = this.log.scrollHeight;
    this.fadeLater(line);
  }

  /** Lights the line up again and fades it `FADE_AFTER_MS` from now. */
  private fadeLater(line: HTMLElement): void {
    line.classList.remove('chat__line--faded');
    window.clearTimeout(this.fades.get(line));
    this.fades.set(
      line,
      window.setTimeout(() => {
        line.classList.add('chat__line--faded');
      }, FADE_AFTER_MS),
    );
  }

  /** `carry`: a zone change, whose next chat picks up the log where this one stops. */
  destroy({ carry = false }: { carry?: boolean } = {}): void {
    this.unfollow();
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('pointerdown', this.onPointerDown, true);
    carried = carry
      ? {
          lines: [...this.log.children].filter((line) => line instanceof HTMLElement),
          shown: this.shown,
          target: this.target,
          lastWhisperer: this.lastWhisperer,
          recent: this.recent,
        }
      : undefined;
    this.root.remove();
  }
}
